// Typed client for the Studio Voice engines API (/v1/studio/voice-engines/*,
// plan §6.6). Mirrors app/models/voice_engine_studio.py. The envelope is
// camelCase; a policy (the Remote Config `voice_engine` JSON) and a profile's
// `settings` keep their stored snake_case shape.
//
// Every write can be refused with 403 by the VOICE_ENGINE_ADMIN_UIDS
// allowlist, which fails closed on prod (`viewer.canWrite` says so up front).

import { apiRequest } from "./http";

export type EngineName = "cascade" | "gemini_live" | "elevenlabs";
export type Publisher = "studio" | "break_glass" | "external";
export type WriteGate = "allowlist" | "open" | "closed";
export type ClientPlatform = "ios" | "android" | "legacy";
export type LadderOutcome = "passed" | "decided" | "not_reached";
export type ProfileStatus = "active" | "archived";

// ---------------------------------------------------------------- policy

export interface PolicyArm {
  key: string;
  engine: EngineName;
  profile: string | null; // null: the engine's built-in default
  pct: number;
  min_build_ios: number | null; // null: the policy's floor
  min_build_android: number | null;
}

// The policy as the server runs it (normalized: every field present).
export interface Policy {
  enabled: boolean;
  min_build_ios: number;
  min_build_android: number;
  experiment: string | null; // null only for a v1 value
  arms: PolicyArm[];
}

export interface TemplateVersion {
  number: string | null;
  updateTime: string | null;
  updateUser: string | null;
  updateOrigin: string | null;
  updateType: string | null;
  description: string | null;
  publisher: Publisher;
  by: string | null;
  note: string | null;
}

export interface PolicyValue {
  raw: string | null;
  value: Record<string, unknown> | null;
  policy: Policy;
  valid: boolean;
  error: string | null;
  isV1: boolean;
}

export interface HistoryEntry extends PolicyValue {
  version: TemplateVersion;
  live: boolean;
}

export interface PolicyState extends PolicyValue {
  etag: string | null;
  templateVersion: TemplateVersion;
  conditional: boolean;
  lastChange: HistoryEntry | null;
  externalPublish: boolean;
  historyError: string | null;
}

export interface DiffRow {
  field: string;
  current: unknown;
  draft: unknown;
}

export interface PolicyHistory {
  entries: HistoryEntry[];
  scanned: number;
  complete: boolean;
}

export interface PublishResult {
  templateVersion: TemplateVersion;
  etag: string | null;
  published: PolicyValue;
  warnings: string[];
  restoredFrom: string | null;
}

// ---------------------------------------------------------------- dry run

export interface ClientInfo {
  platform: ClientPlatform;
  build: number | null;
  seenAt: string | null;
  fromSighting: boolean;
}

export interface Decision {
  engine: EngineName;
  profileKey: string;
  profileVersion: number; // 0: the built-in default
  settings: Record<string, unknown>;
  source: string;
  step: number;
  arm: string | null;
  experiment: string | null;
  policyVersion: string | null;
  reason: string | null;
  requestedEngine: string | null;
  requestedProfile: string | null;
}

export interface LadderStep {
  step: number;
  source: string;
  label: string;
  outcome: LadderOutcome;
  detail: string;
}

export interface Override {
  uid: string;
  engine: EngineName;
  profileKey: string | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string | null;
  expiresAt: string | null;
  active: boolean;
}

export interface DryRun {
  uid: string;
  client: ClientInfo;
  keyboard: boolean;
  bucket: number;
  decision: Decision;
  ladder: LadderStep[];
  override: Override | null;
  policyVersion: string | null;
}

export interface ArmCount {
  key: string;
  engine: EngineName;
  profile: string | null;
  pct: number;
  bucketFrom: number;
  bucketTo: number;
  users: number;
  served: number;
}

export interface Distribution {
  days: number;
  since: string;
  users: number;
  truncated: boolean;
  arms: ArmCount[];
  rest: number;
  bySource: Record<string, number>;
  byEngine: Record<string, number>;
  reasons: Record<string, number>;
}

export interface LookupRequest {
  uid?: string;
  email?: string;
  platform?: ClientPlatform;
  build?: number;
  keyboard?: boolean;
}

export interface Preview {
  valid: boolean;
  errors: string[];
  warnings: string[];
  policy: Policy;
  raw: string;
  distribution: Distribution | null;
  lookup: DryRun | null;
}

// ---------------------------------------------------------------- overview

export interface EngineEnv {
  environment: string;
  s2sEnabled: boolean;
  allowedEngines: string[];
  defaultEngine: string;
  envOverrideUids: number;
  policySource: string;
  firebaseProject: string | null;
}

export interface Viewer {
  uid: string;
  canWrite: boolean;
  writeGate: WriteGate;
}

export interface Counts {
  total: number;
  active: number;
  byEngine: Record<string, number>;
}

export interface Overview {
  env: EngineEnv;
  viewer: Viewer;
  engines: EngineName[];
  policy: PolicyState | null;
  policyError: string | null;
  inForce: { source: string; templateVersion: string | null; policy: Policy };
  profiles: Counts;
  overrides: Counts;
}

// ---------------------------------------------------------------- profiles

export interface ProfileUsage {
  arms: string[];
  overrides: number;
}

export interface Profile {
  key: string;
  engine: EngineName;
  version: number; // 0: a built-in default
  settings: Record<string, unknown>;
  status: ProfileStatus;
  note: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  builtin: boolean;
  usage: ProfileUsage;
}

// The subset of JSON Schema pydantic emits for the settings models.
export interface JsonSchemaProperty {
  type?: string;
  title?: string;
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
  minLength?: number;
  items?: JsonSchemaProperty;
  anyOf?: JsonSchemaProperty[];
  default?: unknown;
}

export interface SettingsSchema {
  properties?: Record<string, JsonSchemaProperty>;
}

export interface EngineInfo {
  engine: EngineName;
  builtinKey: string;
  allowed: boolean;
  defaults: Record<string, unknown>;
  settingsSchema: SettingsSchema;
}

export interface ProfilesResponse {
  profiles: Profile[];
  engines: EngineInfo[];
}

export interface ProfileVersion {
  version: number;
  engine: EngineName;
  settings: Record<string, unknown>;
  note: string | null;
  createdBy: string | null;
  createdAt: string | null;
}

export interface ProfileVersions {
  key: string;
  builtin: boolean;
  versions: ProfileVersion[];
}

// ---------------------------------------------------------------- test users

export interface OverrideRow extends Override {
  email: string | null;
  client: ClientInfo;
  nextCall: Decision;
}

export interface OverridesResponse {
  overrides: OverrideRow[];
  active: number;
  env: { uids: string[]; defaultEngine: string };
}

export interface PutOverrideBody {
  engine: EngineName;
  profileKey: string | null;
  expiresAt: string | null;
  note: string | null;
}

export interface UserLookup {
  uid: string;
  email: string | null;
  displayName: string | null;
  disabled: boolean;
  client: ClientInfo;
  lastCallAt: string | null;
  override: Override | null;
  nextCall: DryRun;
  withoutOverride: Decision;
}

// ---------------------------------------------------------------- calls

const BASE = "/studio/voice-engines";

export function getVoiceEngines(): Promise<Overview> {
  return apiRequest<Overview>(BASE);
}

export function previewPolicy(policy: Record<string, unknown>, lookup?: LookupRequest): Promise<Preview> {
  return apiRequest<Preview>(`${BASE}/policy/preview`, { method: "POST", body: { policy, lookup: lookup ?? null } });
}

export function publishPolicy(policy: Record<string, unknown>, etag: string, note: string): Promise<PublishResult> {
  return apiRequest<PublishResult>(`${BASE}/policy`, { method: "PUT", body: { policy, etag, note } });
}

export function getPolicyHistory(limit = 20): Promise<PolicyHistory> {
  return apiRequest<PolicyHistory>(`${BASE}/policy/history?limit=${limit}`);
}

export function restorePolicy(version: string, etag: string, note: string): Promise<PublishResult> {
  return apiRequest<PublishResult>(`${BASE}/policy/restore`, { method: "POST", body: { version, etag, note } });
}

export function getProfiles(): Promise<ProfilesResponse> {
  return apiRequest<ProfilesResponse>(`${BASE}/profiles`);
}

export function getProfileVersions(key: string): Promise<ProfileVersions> {
  return apiRequest<ProfileVersions>(`${BASE}/profiles/${encodeURIComponent(key)}/versions`);
}

export function createProfile(body: {
  key: string;
  engine: EngineName;
  settings: Record<string, unknown>;
  note: string | null;
}): Promise<Profile> {
  return apiRequest<Profile>(`${BASE}/profiles`, { method: "POST", body });
}

export function saveProfile(
  key: string,
  expectedVersion: number,
  settings: Record<string, unknown>,
  note: string | null,
): Promise<Profile> {
  return apiRequest<Profile>(`${BASE}/profiles/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: { expectedVersion, settings, note },
  });
}

export function setProfileStatus(key: string, expectedVersion: number, status: ProfileStatus): Promise<Profile> {
  return apiRequest<Profile>(`${BASE}/profiles/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: { expectedVersion, status },
  });
}

export function getOverrides(): Promise<OverridesResponse> {
  return apiRequest<OverridesResponse>(`${BASE}/overrides`);
}

export function putOverride(uid: string, body: PutOverrideBody): Promise<OverrideRow> {
  return apiRequest<OverrideRow>(`${BASE}/overrides/${encodeURIComponent(uid)}`, { method: "PUT", body });
}

export function deleteOverride(uid: string): Promise<{ uid: string; deleted: boolean }> {
  return apiRequest(`${BASE}/overrides/${encodeURIComponent(uid)}`, { method: "DELETE" });
}

export function lookupUser(query: { email: string } | { uid: string }): Promise<UserLookup> {
  return apiRequest<UserLookup>(`${BASE}/users/lookup?${new URLSearchParams(query)}`);
}
